import { loginRequestSchema } from '@kominiarz/shared';
import { LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { safeNext } from '../auth/RequireAuth';
import { useLogin, useMe } from '../auth/useAuth';
import { Alert } from '../components/Alert';
import { BrandMark } from '../components/BrandMark';
import { Button } from '../components/Button';
import { PasswordInput } from '../components/form/PasswordInput';
import { TextInput } from '../components/form/TextInput';
import { ApiError, errorMessage } from '../lib/api';
import styles from './LoginPage.module.css';
import { StatusBanner } from '../components/StatusBanner';

export function LoginPage() {
  const me = useMe();
  const loginMutation = useLogin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (me.data?.user) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = loginRequestSchema.safeParse({ login, password });
    if (!parsed.success) {
      setFieldErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setFieldErrors({});
    try {
      await loginMutation.mutateAsync(parsed.data);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fields);
    }
  }

  const error = loginMutation.error;
  const showAlert = error && !(error instanceof ApiError && Object.keys(error.fields).length > 0);

  return (
    <div className={styles.page}>
      <header className={styles.brand}>
        <BrandMark size={48} />
        <span className={styles.brandName}>Kalendarz wizyt</span>
      </header>
      <main className={styles.card}>
        <StatusBanner />
        <h1 className={styles.title}>Zaloguj się</h1>
        <form className={styles.form} onSubmit={submit} noValidate>
          <TextInput
            label="Login"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            error={fieldErrors.login}
          />
          <PasswordInput
            label="Hasło"
            name="password"
            autoComplete="current-password"
            enterKeyHint="go"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
          />
          {showAlert && <Alert kind="error">{errorMessage(error)}</Alert>}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            icon={<LogIn aria-hidden="true" />}
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? 'Logowanie…' : 'Zaloguj się'}
          </Button>
        </form>
        <p className={styles.help}>Nie pamiętasz hasła? Poproś kierownika o ustawienie nowego.</p>
      </main>
    </div>
  );
}
